import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useBreakpointsContext } from "@air/provider-media-query";
import classNames from "classnames";
import isNumber from "lodash/isNumber";
import { memo, useEffect, useRef, useState } from "react";
import { VIEW_CONTROLS } from "~/constants/testIDs";
import { SEARCH_STATIC_HEADER_Z_INDEX } from "~/constants/WorkspaceSpacing";
import { useTemporaryFeatureFlags } from "~/hooks/useTemporaryFeatureFlags";
let ticking = false;
export const ViewControls = /*#__PURE__*/ memo((param)=>{
    let { header, onScrolled, buttons, leftAdornment, headerRef, scrollRef, topPosition = 0, style } = param;
    const { searchImprovements } = useTemporaryFeatureFlags();
    const [_, forceRerender] = useState(0);
    const viewControlsRef = useRef(null);
    const _isScrolledToTopRef = useRef();
    const bottomOfHeader = (headerRef === null || headerRef === void 0 ? void 0 : headerRef.current) ? headerRef.current.clientTop + headerRef.current.clientHeight : 0;
    const { isAboveMediumScreen } = useBreakpointsContext();
    /**
     * On first render, the headerRef.current is undefined, thus bottomOfHeader is too.
     * We want to cause 1 additional render to set the value of headerRef bottomOfHeader
     */ useEffect(()=>{
        if (!bottomOfHeader) {
            forceRerender((s)=>s + 1);
        }
    }, [
        bottomOfHeader
    ]);
    useEffect(()=>{
        const scrolledContainer = scrollRef === null || scrollRef === void 0 ? void 0 : scrollRef.current;
        const handleScroll = ()=>{
            var _viewControlsRef_current;
            const viewControlsRect = (_viewControlsRef_current = viewControlsRef.current) === null || _viewControlsRef_current === void 0 ? void 0 : _viewControlsRef_current.getBoundingClientRect();
            const topOfViewControls = viewControlsRect === null || viewControlsRect === void 0 ? void 0 : viewControlsRect.top;
            if (isNumber(topOfViewControls) && isNumber(bottomOfHeader)) {
                const isScrolledToTop = topOfViewControls === bottomOfHeader + topPosition;
                /**
           * We only want to trigger a re-render if the value has actually changed,
           * so we store a local copy to compare it against
           */ if (_isScrolledToTopRef.current !== isScrolledToTop) {
                    _isScrolledToTopRef.current = isScrolledToTop;
                    onScrolled === null || onScrolled === void 0 ? void 0 : onScrolled(isScrolledToTop);
                }
            }
        };
        const debouncedScroll = ()=>{
            if (!ticking) {
                window.requestAnimationFrame(()=>{
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };
        !!onScrolled && (scrolledContainer === null || scrolledContainer === void 0 ? void 0 : scrolledContainer.addEventListener("scroll", debouncedScroll));
        return ()=>{
            scrolledContainer === null || scrolledContainer === void 0 ? void 0 : scrolledContainer.removeEventListener("scroll", debouncedScroll);
        };
    }, [
        bottomOfHeader,
        headerRef,
        _isScrolledToTopRef,
        onScrolled,
        scrollRef,
        topPosition
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: classNames("border-b border-b-grey-4 bg-grey-1 transition-all duration-200 ease-out", searchImprovements ? "mt-2 py-2" : "py-3 md:pt-0"),
        ref: viewControlsRef,
        "data-testid": VIEW_CONTROLS,
        onClick: (e)=>e.stopPropagation(),
        style: {
            zIndex: isAboveMediumScreen ? SEARCH_STATIC_HEADER_Z_INDEX : 1,
            position: isAboveMediumScreen && !!bottomOfHeader ? "sticky" : "relative",
            // Top position to align headers with open filters, and directly beneath HeaderBar
            top: isAboveMediumScreen ? bottomOfHeader : 0,
            ...style
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center justify-between",
            children: [
                leftAdornment,
                /*#__PURE__*/ _jsx("div", {
                    className: "flex-1",
                    children: header
                }),
                !!buttons && /*#__PURE__*/ _jsx("div", {
                    className: "flex items-center justify-end",
                    children: buttons
                })
            ]
        })
    });
});
ViewControls.displayName = "ViewControls";
